import { useState } from 'react'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import TaskCard from '@/components/feature/TaskCard'
import TaskCreateSheet from '@/components/feature/TaskCreateSheet'
import SelectionBar from '@/components/ui/selection-bar'
import { useCompleteTask, useDeleteTask, useTasks } from '@/hooks/useTasks'
import { useSelectionMode } from '@/hooks/useSelectionMode'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { deleteTask as deleteTaskApi } from '@/api/tasks'
import { useQueryClient } from '@tanstack/react-query'
import { sortTasks } from '@/lib/task-utils'
import type { Task } from '@/types'

export default function TaskListPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<Task | null>(null)
  const { data: rawTasks = [], isLoading } = useTasks()
  const sorted = sortTasks(rawTasks)
  const activeTasks = sorted.filter((t) => t.is_completable)
  const pendingTasks = sorted.filter((t) => !t.is_completable && !t.is_completed)
  // 表示対象（未完了/予定）が1件でもあるか。完了済みだけ残っていても空状態を出す
  const hasVisibleTasks = activeTasks.length > 0 || pendingTasks.length > 0
  const completeTask = useCompleteTask()
  const deleteTask = useDeleteTask()
  const selection = useSelectionMode()
  const isPC = useMediaQuery('(min-width: 768px)')
  const qc = useQueryClient()

  const handleComplete = (id: number) => {
    completeTask.mutate(id, {
      onSuccess: () => toast.success('実績を登録しました！'),
      onError: () => toast.error('エラーが発生しました'),
    })
  }

  const handleDelete = (id: number) => {
    deleteTask.mutate(id, {
      onError: () => toast.error('削除できませんでした'),
    })
  }

  const handleBulkDelete = async () => {
    await Promise.all(selection.selectedIds.map((id) => deleteTaskApi(id)))
    qc.invalidateQueries({ queryKey: ['tasks'] })
    toast.success(`${selection.selectedIds.length}件を削除しました`)
    selection.clear()
  }

  const taskCardProps = (task: Task) => ({
    task,
    onComplete: handleComplete,
    onDelete: handleDelete,
    onEdit: selection.isSelecting ? undefined : setEditTarget,
    isCompleting: completeTask.isPending,
    isSelecting: selection.isSelecting,
    isSelected: selection.selectedIds.includes(task.id),
    onSelect: selection.toggle,
    onLongPress: !isPC ? selection.enter : undefined,
  })

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground text-sm">読み込み中...</div>
  }

  return (
    <div className="py-4">
      {isPC && !selection.isSelecting && hasVisibleTasks && (
        <div className="flex justify-end mb-3">
          <Button variant="ghost" size="sm" onClick={selection.enter}>選択</Button>
        </div>
      )}

      {!hasVisibleTasks ? (
        <div className="py-16 text-center text-muted-foreground">
          <p className="text-sm">タスクが登録されていません</p>
          <p className="text-xs mt-1">右下の＋からタスクを追加してみましょう</p>
        </div>
      ) : (
        <div className="space-y-3">
          {activeTasks.map((task) => (
            <TaskCard key={task.id} {...taskCardProps(task)} />
          ))}

          {pendingTasks.length > 0 && (
            <>
              <p className="text-xs font-medium text-muted-foreground pt-2">予定タスク</p>
              {pendingTasks.map((task) => (
                <TaskCard key={task.id} {...taskCardProps(task)} />
              ))}
            </>
          )}
        </div>
      )}

      {!selection.isSelecting && (
        <Button
          onClick={() => setCreateOpen(true)}
          size="icon"
          className="fixed bottom-20 right-4 h-12 w-12 rounded-full shadow-lg"
        >
          <Plus size={24} />
        </Button>
      )}

      {selection.isSelecting && (
        <SelectionBar
          count={selection.selectedIds.length}
          onDelete={handleBulkDelete}
          onCancel={selection.clear}
        />
      )}

      <TaskCreateSheet open={createOpen} onClose={() => setCreateOpen(false)} />
      <TaskCreateSheet
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        task={editTarget ?? undefined}
      />
    </div>
  )
}
